import {
  Database, BarChart3, ShieldCheck, Globe, Layers, Users, Clock,
  Award, Building2, Truck, Heart, ShoppingBag, Briefcase,
  TrendingUp, CreditCard, Package, Plug, Server,
  Target, Handshake, LayoutDashboard, RefreshCw, FileCheck,
  Settings, ArrowUpRight, Factory, Utensils, Stethoscope,
  ChevronDown, ChevronUp, Cpu, Zap
} from "lucide-react";

export const ERP_HERO = {
  eyebrow: "Microsoft Dynamics 365 Business Central",
  headline: "Integrated Business Systems for Growing SMEs",
  subheadline:
    "When systems don't talk, businesses don't scale. Asterisk helps SMEs move from disconnected tools to an integrated, intelligent business platform — powered by Microsoft.",
  primaryCTA: { label: "Book an ERP Consultation", href: "#erp-contact" },
  secondaryCTA: { label: "Explore Capabilities", href: "#erp-modules" },
};

export const ERP_STATS = [
  { value: "180+", label: "Projects Deployed", icon: Layers },
  { value: "24", label: "Years Microsoft ERP", icon: Clock },
  { value: "15", label: "Industries Served", icon: Building2 },
  { value: "18", label: "Experienced Consultants", icon: Users },
];

export const ERP_PROBLEM = {
  headline: "When Systems Don't Talk, Businesses Don't Scale",
  description:
    "Many small and medium enterprises reach a familiar stage — accounting in one system, sales tracked in spreadsheets, inventory managed manually, customer data scattered across teams, and reporting done at month-end if time allows.",
  symptoms: [
    "Slow decisions from fragmented data",
    "Data duplication across departments",
    "Revenue leakage from manual processes",
    "Operational stress from disconnected tools",
    "Month-end reporting bottlenecks",
    "No single source of truth",
  ],
};

export const ERP_MODULES = [
  { icon: CreditCard, title: "Finance & Accounting", desc: "Real-time financial visibility with audit-ready structures" },
  { icon: Package, title: "Procurement", desc: "Streamlined purchasing with approval workflows" },
  { icon: Truck, title: "Inventory & Supply Chain", desc: "Accurate stock tracking and demand planning" },
  { icon: TrendingUp, title: "Sales & Distribution", desc: "Order-to-cash automation and sales analytics" },
  { icon: Building2, title: "Fixed Assets", desc: "Lifecycle management and depreciation tracking" },
  { icon: Briefcase, title: "Project Accounting", desc: "Job costing and project profitability analysis" },
  { icon: Globe, title: "Multi-entity & Multi-currency", desc: "Regional operations with consolidated reporting" },
  { icon: ShieldCheck, title: "GST/VAT Compliance", desc: "Automated tax calculations and filing readiness" },
];

export const CRM_CAPABILITIES = [
  { icon: Users, title: "360° Customer Visibility", desc: "Complete view of every customer interaction" },
  { icon: Target, title: "Lead-to-Cash Tracking", desc: "From first touch to revenue, fully tracked" },
  { icon: BarChart3, title: "Sales Pipeline Management", desc: "Forecast and manage deals with confidence" },
  { icon: Zap, title: "Marketing Automation", desc: "Nurture campaigns and engagement scoring" },
  { icon: Handshake, title: "Customer Service Cases", desc: "Resolve issues faster with case management" },
  { icon: RefreshCw, title: "Recurring Revenue Tracking", desc: "Subscription and contract management" },
];

export const INTEGRATION_BENEFITS = [
  "Sales forecasts align with inventory planning",
  "Credit controls integrate into sales approvals",
  "Customer profitability becomes measurable",
  "Service contracts link to billing automation",
  "Management dashboards reflect live operational data",
];

export const ERP_INDUSTRIES = [
  { name: "Healthcare & Long-Term Care", icon: Stethoscope },
  { name: "Non-Profit Organisations", icon: Heart },
  { name: "Distribution & Trading", icon: Truck },
  { name: "Manufacturing", icon: Factory },
  { name: "Hospitality", icon: Utensils },
  { name: "Retail", icon: ShoppingBag },
  { name: "Professional Services", icon: Briefcase },
];

export const ERP_REGIONAL = [
  "Multi-country taxation",
  "Multi-currency consolidation",
  "Intercompany transactions",
  "Regional reporting governance",
];

export const ERP_APPROACH = [
  { step: "01", title: "Business Process Review", desc: "Understand your operations and pain points" },
  { step: "02", title: "Solution Architecture", desc: "Design the right system for your needs" },
  { step: "03", title: "Phased Implementation", desc: "Go live in manageable stages" },
  { step: "04", title: "Data Migration", desc: "Clean, structured transfer of your data" },
  { step: "05", title: "Training & Change Mgmt", desc: "Empower your team with knowledge" },
  { step: "06", title: "Post-Go-Live Support", desc: "Ongoing optimisation and support" },
];

export const ERP_OUTCOMES = [
  "Operational clarity",
  "Real-time management visibility",
  "Financial control",
  "Scalable digital infrastructure",
  "Stronger compliance posture",
  "Increased customer retention",
  "Faster decision cycles",
];

export const ERP_MODERNISE = [
  "Migrating from legacy accounting software",
  "Replacing spreadsheets with integrated systems",
  "Expanding regionally across ASEAN",
  "Integrating finance and sales operations",
  "Preparing for AI-driven analytics",
];

export const ERP_FAQ = [
  {
    q: "What ERP system does Asterisk implement?",
    a: "Asterisk implements Microsoft Dynamics 365 Business Central — a comprehensive cloud-based ERP solution designed for SMEs. It covers finance, procurement, inventory, sales, fixed assets, project accounting, and multi-entity management.",
  },
  {
    q: "Can Asterisk implement both ERP and CRM together?",
    a: "Yes. We specialise in integrated ERP + CRM deployments where sales, finance, and operations work from a single platform. This integration provides real-time visibility and eliminates data silos between departments.",
  },
  {
    q: "Which industries does Asterisk serve for ERP?",
    a: "We have deep implementation experience across healthcare, non-profits, distribution, manufacturing, hospitality, retail, and professional services — with over 180 deployments across 15 industries.",
  },
  {
    q: "Does Asterisk support regional multi-country ERP deployments?",
    a: "Absolutely. We design ERP architectures that support multi-country taxation, multi-currency consolidation, intercompany transactions, and regional reporting governance across Singapore, Malaysia, Philippines, and broader ASEAN.",
  },
  {
    q: "How long does an ERP implementation take?",
    a: "Implementation timelines vary by scope, but a typical SME deployment takes 3–6 months using our phased approach. We ensure minimal operational disruption with structured data migration and change management.",
  },
  {
    q: "Does Asterisk provide post-go-live support?",
    a: "Yes. Long-term support is part of our DNA. We don't disappear after go-live — many of our clients have been with us for 10–20 years. We provide ongoing optimisation, training, and system enhancements.",
  },
];
